import React from 'react';
import styled from 'styled-components';
import * as Sentry from '@sentry/react';
import LoadingScreen from './LoadingScreen';
import {
  isChunkLoadError,
  shouldReload,
  readLastReload,
  writeLastReload
} from './chunkLoadError';

/**
 * Error boundary that recovers from lazy-loaded chunk failures.
 *
 * Background: routes are code-split via `lazy(() => import(...))`. After a
 * frontend deploy, content-hashed chunk filenames change and the previous
 * build's chunks are no longer served. A still-open tab (or a client holding a
 * cached index.html) then requests an orphaned chunk; nginx returns 404 and the
 * dynamic import rejects with a ChunkLoadError, which React throws during
 * render. With no boundary this unmounts the whole tree → blank page (this is
 * what produced the blank "Proceed to Checkout" screen).
 *
 * Recovery: on a chunk-load error, force a single full reload so the browser
 * pulls the current index.html + current chunks. A sessionStorage timestamp
 * guards against reload loops if the asset is genuinely gone post-reload.
 *
 * Non-chunk errors are re-thrown so we never mask real application bugs.
 *
 * Pure detection/guard helpers live in ./chunkLoadError.js (JSX-free so they
 * are unit-testable under the repo's Jest config).
 */

const FallbackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 24px;
  text-align: center;

  h4 {
    margin-bottom: 16px;
  }

  button {
    margin-top: 8px;
    padding: 12px 24px;
    cursor: pointer;
  }
`;

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, isChunk: false, guarded: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error, isChunk: isChunkLoadError(error), guarded: false };
  }

  componentDidCatch(error, info) {
    if (!isChunkLoadError(error)) {
      // Not a chunk failure — re-thrown in render(); don't handle here.
      return;
    }

    Sentry.captureException(error, {
      tags: { chunkLoadError: true },
      extra: { componentStack: info && info.componentStack }
    });

    const now = Date.now();
    if (shouldReload(now, readLastReload())) {
      writeLastReload(now);
      window.location.reload();
      return;
    }

    // Already reloaded recently — show the fallback instead of looping.
    this.setState({ guarded: true });
  }

  handleReload() {
    writeLastReload(Date.now());
    window.location.reload();
  }

  render() {
    const { error, isChunk, guarded } = this.state;

    if (error) {
      // Re-throw non-chunk errors so real bugs are not masked.
      if (!isChunk) throw error;

      // Chunk error, reload already attempted within the guard window.
      if (guarded) {
        return (
          <FallbackWrapper>
            <div>
              <h4>Something went wrong loading this page.</h4>
              <h4>Please refresh to continue.</h4>
              <button type="button" onClick={this.handleReload}>
                Refresh
              </button>
            </div>
          </FallbackWrapper>
        );
      }

      // Chunk error, reload in flight — show the normal loading state.
      return <LoadingScreen />;
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
